import {computed, inject, ref, watch, onMounted} from "vue";
import {useI18n} from "vue-i18n";

export function useFormControl(props, emit) {
    const inputValue = ref(getInputValue(props.modelValue));
    const formData = inject('form:data', {});
    const formErrors = inject('form:errors', null);
    const addField = inject('form:add-field', null);
    const registerFieldToTab = inject('tab:register-form-field', null)
    const checkFormData = computed(() =>
        typeof formData !== 'undefined' && typeof props.name !== 'undefined' && !props.ignoreFormData
    );

    if (checkFormData.value) {

        if (registerFieldToTab && props.name) {
            registerFieldToTab(props.name);
        }

        if (addField && props.name) {
            addField(props.name);
            if (props.errorField) {
                addField(props.errorField);
            }
        }

        let formValue = getFormDataValue(props.name, formData);
        if (typeof formValue !== 'undefined') {
            inputValue.value = getInputValue(formValue);
            emit('update:modelValue', getModelValue(formValue));
        }

        watch(
        () => getFormDataValue(props.name, formData),
        (val) => {
            const value = getInputValue(val);
            if (Array.isArray(value)) {
                // Compare two arrays
                if (JSON.stringify(value).toString() === JSON.stringify(inputValue.value || []).toString()) return;
            } else if (value === inputValue.value) {
                return;
            }
            inputValue.value = value;
        });
    }

    watch(
        () => props.modelValue,
        (value) => {
            inputValue.value = getInputValue(value);
        }
    ,{ deep: props.multiple });

    watch(
        () => inputValue.value,
        (value) => {
            value = getModelValue(value);
            emit('update:modelValue', value);

            if (checkFormData.value) {
                setFormDataValue(props.name, value);
            }
        }
    , { deep: props.multiple });

    function getModelValue(value) {
        if (typeof props.trueValue !== 'undefined') {
            return value && value !== '0' ? props.trueValue : props.falseValue;
        }
        return value;
    }

    function getInputValue(value) {
        if (typeof props.trueValue !== 'undefined') {
            return ((typeof value === 'undefined' || value === null) ? '' : value).toString() === props.trueValue.toString();
        }
        return value;
    }

    function setFormDataValue(name, value) {
        const keys = name.split('.');
        let key = keys[keys.length - 1];
        let field = formData;

        if (keys.length > 1) {
            if (key === '*') {
                keys.splice(keys.length - 1);
                key = keys[keys.length - 1];
            }
            keys.splice(keys.length - 1);
            keys.forEach(key => {
                if (!field) return;
                field = field[key];
            });
        } else {
            key = keys[0];
        }

        if (typeof field === 'undefined' || field === null) return;

        field[key] = value;
    }

    return {
        inputValue,
        error: computed(() => {
            if (props.name) {
                if (formErrors && (formErrors[props.errorField] || formErrors[props.name] || formErrors[`selected_${props.name}`])) {
                    return formErrors[props.errorField] || formErrors[props.name] || formErrors[`selected_${props.name}`];
                }
            }

            return props.error;
        })
    }
}

export function getFormDataValue(name, formData) {
    let field = formData;
    name.split('.').forEach(key => {
        if (key === '*') return false;
        field = field && typeof field[key] !== 'undefined' ? field[key] : undefined;

        if (typeof field === 'undefined') return false;
    });
    return field;
}

export function useFormControlParser(props, options = {}) {
    const { t, te } = useI18n();
    const parsedLabel = computed(() => parseLabel(props.label, props.name));

    function parseLabel(label, name) {
        if (label) return te(label) ? t(label) : label;
        if (!name) return;
        name = parseFieldName(name);
        const key = `validation.attributes.${name}`;
        return te(key) ? t(key) : null;
    }

    options = Object.assign({
        defaultPlaceholder: t('validation.input_placeholder')
    }, options);

    return {
        parsedName: computed(() => {
            if (props.disableNameParser) return props.name || null;
            if (!props.name) return null;
            let name = props.name;
            if (name && name.match(/[.]/)) {
                return name.split('.').map((key, index) => {
                    key = key.replace('|', '.');
                    return index ? (key === '*' ? '[]' : `[${key}]`) : key;
                }).join('');
            }
            return `${name}${props.multiple || props.range ? '[]' : ''}`
        }),
        parsedPlaceholder: computed(() => {
            if (props.placeholder) return props.placeholder;
            return `${ options.defaultPlaceholder } ${ (parsedLabel.value || '').toLowerCase() }...`;
        }),
        parsedLabel,
        parseLabel,
    }
}

function parseFieldName(name) {
    let field = '';
    name.split('.').forEach((key, index) => {
        if (key === '*') return;
        field += index ? `[${key}]` : key;
    });
    return field;
}